<script>
  import Tooltip from "@components/tooltip.svelte";
  import { buildTotals, totalsFor, uncoveredDutyDates, uncoveredOpeningDates, formatNumber, pluralize } from "@lib/presence-totals";

  let { users, clinic, month, dates, userType, presences, absences, dutyShifts, clinicOpenings, onClose } = $props();

  // Presence figures for this clinic only, or across every clinic the users worked at
  let allClinics = $state(false);

  // Presence shown in days (rounded to half days) or in exact hours
  let inHours = $state(false);

  let totals = $derived(buildTotals({ presences, absences, dutyShifts, clinicOpenings, clinicId: clinic.id, allClinics }));
  let rows = $derived(users.map((user) => ({ user, total: totalsFor(totals, user) })));

  let teamPresenceDays = $derived(rows.reduce((sum, row) => sum + row.total.presenceDays, 0));
  let teamPresenceHours = $derived(rows.reduce((sum, row) => sum + row.total.hours, 0));
  let teamAbsenceDays = $derived(rows.reduce((sum, row) => sum + row.total.absenceDays, 0));

  let gapDates = $derived(
    userType === "vet" ? uncoveredDutyDates(dates, dutyShifts)
    : userType === "aux" ? uncoveredOpeningDates(dates, clinicOpenings)
    : []
  );
  let coverageDays = $derived(userType === "aux" ? dates.filter((date) => date.getDay() !== 0).length : dates.length);

  let monthLabel = $derived(month.toLocaleString("fr", { month: "long", year: "numeric" }));
  let scopeLabel = $derived(allClinics ? "Toutes cliniques" : clinic.name);

  function gapText() {
    const noun = userType === "vet" ? "nuit" : "jour";
    const what = userType === "vet" ? "sans garde" : "sans ouverture";
    const count = `${gapDates.length} ${noun}${gapDates.length > 1 ? "s" : ""} ${what}`;
    if (gapDates.length > 6) return count;
    const days = gapDates.map((date) => Number(date.slice(8, 10))).join(", ");
    return `${count} : ${days} ${month.toLocaleString("fr", { month: "short" })}`;
  }

  function presenceText(total) {
    return inHours ? `${formatNumber(total.hours)} h` : `${formatNumber(total.presenceDays)} j`;
  }

  function periodsHint(total) {
    return `${pluralize(total.fullDays, "journée")} et ${pluralize(total.halfDays, "demi-journée")}`;
  }

  function clinicBreakdown(total, hours = inHours) {
    const values = hours ? total.byClinicHours : total.byClinic;
    return Object.entries(values)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => `${name} ${formatNumber(value)} ${hours ? "h" : "j"}`)
      .join(" · ");
  }

  function downloadCsv() {
    const header = ["Utilisateur", "Présence (j)", "Journées", "Demi-journées", "Heures", "Absences (j)", "Gardes", "Gardes week-end", "Ouvertures", "Détail par clinique"];
    const lines = rows.map(({ user, total }) => [
      user.name, total.presenceDays, total.fullDays, total.halfDays, total.hours, total.absenceDays,
      total.dutyShifts, total.weekendDutyShifts, total.openings, clinicBreakdown(total, false)
    ]);
    const csv = [header, ...lines]
      .map((line) => line.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(";"))
      .join("\n");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    link.download = `presences-${dates[0].toISOString().slice(0, 7)}-${allClinics ? "toutes-cliniques" : clinic.name}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }
</script>

<aside class="sticky top-4 w-80 shrink-0 max-h-[calc(100vh-2rem)] overflow-y-auto rounded-sm border border-gray-200 bg-white text-smaller text-gray-700">

  <!-- Title, scope toggle and close button -->
  <header class="flex flex-col gap-3 border-b border-gray-100 px-4 py-3">
    <div class="flex items-start justify-between gap-2">
      <div>
        <h3 class="font-medium text-gray-800 first-letter:uppercase">Résumé · {monthLabel}</h3>
        <p class="text-xs text-gray-500">{scopeLabel}</p>
      </div>
      <button type="button" class="text-gray-400 hover:text-gray-700" aria-label="Fermer le résumé" onclick={onClose}>
        <i class="fal fa-times"></i>
      </button>
    </div>

    <div class="grid grid-cols-2 rounded-sm border border-gray-200 text-xs">
      <button type="button" class={`px-2 py-1 ${!allClinics ? "bg-teal-50 text-teal-700 font-medium" : "text-gray-500"}`} onclick={() => allClinics = false}>Cette clinique</button>
      <button type="button" class={`px-2 py-1 border-l border-gray-200 ${allClinics ? "bg-teal-50 text-teal-700 font-medium" : "text-gray-500"}`} onclick={() => allClinics = true}>Toutes cliniques</button>
    </div>

    <div class="grid grid-cols-2 rounded-sm border border-gray-200 text-xs">
      <button type="button" class={`px-2 py-1 ${!inHours ? "bg-teal-50 text-teal-700 font-medium" : "text-gray-500"}`} onclick={() => inHours = false}>Jours</button>
      <button type="button" class={`px-2 py-1 border-l border-gray-200 ${inHours ? "bg-teal-50 text-teal-700 font-medium" : "text-gray-500"}`} onclick={() => inHours = true}>Heures</button>
    </div>
  </header>

  <!-- Team figures -->
  <section class="flex flex-col gap-3 border-b border-gray-100 px-4 py-3">
    <div class="grid grid-cols-3 gap-2">
      <div class="rounded-sm bg-gray-50 px-2 py-2">
        <p class="text-xs text-gray-500">Présences</p>
        <p class="text-base font-medium text-gray-800">{inHours ? `${formatNumber(teamPresenceHours)} h` : `${formatNumber(teamPresenceDays)} j`}</p>
      </div>
      <div class="rounded-sm bg-gray-50 px-2 py-2">
        <p class="text-xs text-gray-500">Absences</p>
        <p class="text-base font-medium text-gray-800">{formatNumber(teamAbsenceDays)} j</p>
      </div>
      {#if userType === "vet" || userType === "aux"}
        <div class="rounded-sm bg-gray-50 px-2 py-2">
          <p class="text-xs text-gray-500">{userType === "vet" ? "Gardes" : "Ouvertures"}</p>
          <p class="text-base font-medium text-gray-800">{coverageDays - gapDates.length}/{coverageDays}</p>
        </div>
      {/if}
    </div>

    {#if gapDates.length > 0}
      <p class="rounded-sm bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
        <i class="fal fa-exclamation-triangle mr-1"></i>{gapText()}
      </p>
    {/if}
  </section>

  <!-- One row per user -->
  <ul class="divide-y divide-gray-100">
    {#each rows as { user, total } (user.id)}
      <li class="flex flex-col gap-1.5 px-4 py-3">
        <div class="flex items-center justify-between gap-2">
          <span class="flex items-center gap-2 text-gray-800">
            <span class={`inline-block h-2 w-2 rounded-full bg-${user.color}`}></span>
            {user.name}
          </span>
          <Tooltip hint={periodsHint(total)}>
            <span class={`totals-chip bg-${user.color}/20 text-gray-800`}>{presenceText(total)}</span>
          </Tooltip>
        </div>

        <div class="flex flex-wrap gap-1 text-xxs empty:hidden">
          {#if total.absenceDays > 0}
            <span class="totals-chip bg-gray-100 text-gray-500">{formatNumber(total.absenceDays)} j abs</span>
          {/if}
          {#if total.dutyShifts > 0}
            <span class="totals-chip bg-yellow-100 text-yellow-800">{pluralize(total.dutyShifts, "garde")} · {total.weekendDutyShifts} WE</span>
          {/if}
          {#if total.openings > 0}
            <span class="totals-chip bg-yellow-100 text-yellow-800">{pluralize(total.openings, "ouverture")}</span>
          {/if}
        </div>

        {#if allClinics && Object.keys(total.byClinic).length > 0}
          <p class="text-xs text-gray-500">{clinicBreakdown(total)}</p>
        {/if}
      </li>
    {:else}
      <li class="px-4 py-3 text-gray-500">Aucun utilisateur</li>
    {/each}
  </ul>

  <footer class="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
    <span>{userType === "vet" ? "Gardes : toutes cliniques" : "Mis à jour à chaque modification"}</span>
    <button type="button" class="underline hover:text-gray-800" onclick={downloadCsv}>CSV</button>
  </footer>

</aside>
